import {ChangeDetectionStrategy, Component, Input, OnInit, SimpleChanges} from '@angular/core';
import {DayInformation} from 'src/app/shared/models/day-information.model';
import {Task} from 'src/app/shared/models/task.model';
import {getAllCategories, sortCategoriesAndTasks} from "src/app/core/utils/task.utils";
import {StorageService} from "src/app/services/storage.service";
import {TaskFilter} from "src/app/shared/models/task-filter.enum";
import {Weight} from "src/app/shared/models/weight.model";
import {KeyValuePipe, NgClass, NgOptimizedImage} from "@angular/common";
import {TranslatePipe} from "@ngx-translate/core";
import {RouterLink} from "@angular/router";
import {MatCard, MatCardContent, MatCardTitle} from "@angular/material/card";
import {LanguageService} from '../../../services/language.service';

@Component({
    selector: 'app-recommendation-list',
    templateUrl: './recommendation-list.component.html',
    imports: [
        KeyValuePipe,
        TranslatePipe,
        RouterLink,
        MatCard,
        NgClass,
        MatCardTitle,
        MatCardContent,
        NgOptimizedImage
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./recommendation-list.component.scss']
})
export class RecommendationListComponent implements OnInit {

    @Input()
    dayInformation: DayInformation | undefined;

    @Input()
    allTasks = new Array<Task>();
    categories: Map<string, Task[]> = new Map();

    constructor(
        private storage: StorageService,
        public language: LanguageService
    ) {
    }

    _searchText: string = "";

    @Input() set searchText(value: string) {
        this._searchText = value;
        this.initTasks();
    }

    ngOnInit(): void {
        this.initTasks();
    }

    ngOnChanges(_: SimpleChanges) {
        this.initTasks();
    }

    initTasks() {
        const categoriesChosen = this.storage.getDayFilterCategories();

        if (!this.dayInformation) {
            return
        }

        const tasksForDay = this.dayInformation.sign.tasks;
        const allCategories = getAllCategories(this.dayInformation)
        const unsorted: Map<string, Task[]> = new Map();

        for (const category of allCategories) {
            if (!categoriesChosen.includes(category)) {
                continue;
            }

            const tasksForCategory = tasksForDay.filter(task => task.category.name === category);

            const tasksFiltered = this.filterTasks(tasksForCategory);
            if (tasksFiltered.length > 0) {
                unsorted.set(category, tasksFiltered);
            }
        }

        this.addNeutralTasks(unsorted, allCategories, categoriesChosen);
        this.sortCategoriesAndTasks(unsorted);
    }

    sortCategoriesAndTasks(unsorted: Map<string, Task[]>) {
        this.categories = sortCategoriesAndTasks(unsorted)
    }

    addNeutralTasks(unsorted: Map<string, Task[]>, allCategories: string[], categoriesChosen: string[]) {
        const showNeutralTasks = this.storage.getDayFilterShowNeutral();
        if (!showNeutralTasks) {
            return;
        }

        if (!this.dayInformation) {
            return
        }

        const tasksForDay = this.dayInformation.sign.tasks;
        const tasksAvailable = this.allTasks.length > 0 && tasksForDay.length > 0;

        if (tasksAvailable) {
            const neutralTasks = this.getNeutralTasks();
            const neutralTasksFiltered = this.filterTasks(neutralTasks);

            for (const category of allCategories) {
                if (!categoriesChosen.includes(category)) {
                    continue;
                }

                const neutralTasksForCategory = neutralTasksFiltered
                    .filter(task => task.category.name === category)
                    .map(task => ({...task, weight: 'neutral'}));

                if (neutralTasksForCategory.length > 0) {
                    unsorted.set(category, [...(unsorted.get(category) ?? []), ...neutralTasksForCategory]);
                }
            }
        }
    }

    getNeutralTasks(): Task[] {
        if (!this.dayInformation) {
            return [];
        }

        let cloned = this.allTasks.map(x => Object.assign({}, x));
        return cloned.filter(task => !this.dayInformation?.sign.tasks.find(dayTask => dayTask.id === task.id));
    }

    private filterTasks(tasks: Task[]): Task[] {
        const dayTaskFilter = this.storage.getDayFilterTasks()

        return tasks.filter(task => this.shouldShowTask(dayTaskFilter, task));
    }

    private shouldShowTask(dayTaskFilter: TaskFilter, task: Task): boolean {
        const includesSearchText = this._searchText.length == 0 ? true : this.includesSearchText(task)
        const fitsWeightSelection = this.fitsWeightSelection(dayTaskFilter, task)

        return includesSearchText && fitsWeightSelection
    }

    private includesSearchText(task: Task): boolean {
        const header = task.header;
        const description = task.description;

        return header.toLowerCase().includes(this._searchText.toLowerCase()) ||
            description.toLowerCase().includes(this._searchText.toLowerCase());
    }

    private fitsWeightSelection(dayTaskFilter: TaskFilter, task: Task) {
        if (task.weight == null) {
            return true;
        }

        switch (dayTaskFilter) {
            case TaskFilter.All:
                return true;
            case TaskFilter.OnlyVeryGood:
                return task.weight == Weight.best;
            case TaskFilter.OnlyGood:
                return task.weight == Weight.best || task.weight == Weight.good;
            case TaskFilter.OnlyVeryGoodVeryBad:
                return task.weight == Weight.best || task.weight == Weight.worst;
        }
    }
}
